import MailboxFoldersPanel from './MailboxFoldersPanel';
import LineAlertSettings from './LineAlertSettings';
import React, { useEffect, useState } from 'react';
import { supabaseClient as supabase } from '../../config/supabase';
import {
  isValidEmailAllowlistEntry,
  normalizeEmailAllowlistEntry,
  normalizeSenderAllowlist,
  senderAllowlistIncludes,
} from '../../lib/emailAllowlist';
import { mailboxStatusLabel, normalizeSenderBlocklist, suggestEmailProvider } from '../../lib/emailMailbox';
import MessageTemplatesSettingsView from './MessageTemplatesSettingsView';
import VerificationCodeSettings from './VerificationCodeSettings';
import { formatPhone } from '../../lib/phone';

const PROVIDER_LABELS = {
  gmail: 'Gmail (Google Workspace)',
  microsoft_graph: 'Microsoft 365 / Outlook',
  imap_smtp: 'IMAP + SMTP (Generic)',
};

const PROVIDER_DESCRIPTIONS = {
  gmail: 'Connects via the Gmail API using OAuth. Supports push notifications for near-real-time intake.',
  microsoft_graph: 'Connects via the Microsoft Graph API using OAuth. Supports change notifications for near-real-time intake.',
  imap_smtp: 'Generic IMAP inbound + SMTP outbound. Polling-based (you choose how often it is checked: every 1, 5, 10 or 15 minutes). Use for providers outside Gmail and Microsoft 365.',
};

/** How often a plain IMAP mailbox is checked (Gmail and Microsoft are instant and ignore this). Default 1. */
export const MAILBOX_CHECK_MINUTES = [1, 5, 10, 15];
const DEFAULT_IMAP_PORT = 993;
const DEFAULT_SMTP_PORT = 465;

function ProviderBadge({ providerType }) {
  const colors = {
    gmail: 'border border-red-500 text-red-600 bg-transparent',
    microsoft_graph: 'border border-blue-500 text-blue-600 bg-transparent',
    imap_smtp: 'border border-gray-400 text-gray-600 bg-transparent',
  };
  return (
    <span className={'inline-block text-xs font-medium px-2 py-0.5 rounded-full ' + (colors[providerType] ?? 'border border-gray-400 text-gray-600 bg-transparent')}>
      {PROVIDER_LABELS[providerType] ?? providerType}
    </span>
  );
}

function EmailConnectionsPanel({ theme = 'light', orgId = null }) {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [canManage, setCanManage] = useState(false);
  const [entities, setEntities] = useState([]);
  const [accessRoles, setAccessRoles] = useState([]);
  const [accessMembers, setAccessMembers] = useState([]);
  const [newAccessSubject, setNewAccessSubject] = useState('');
  const [accessDrafts, setAccessDrafts] = useState({});
  const [detailSection, setDetailSection] = useState('setup');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [allowlistDrafts, setAllowlistDrafts] = useState({});
  const [pendingAllowlistRemoval, setPendingAllowlistRemoval] = useState(null);
  const [selectedMailboxIds, setSelectedMailboxIds] = useState([]);
  const [setupStep, setSetupStep] = useState(1);

  // Form state for new / editing provider
  const [editing, setEditing] = useState(null); // null = show list; 'new' = new form; row id = edit form
  const [form, setForm] = useState(buildEmptyForm('gmail'));

  useEffect(() => {
    loadProviders();
  }, []);

  useEffect(() => {
    if (!orgId) return;
    Promise.all([
      supabase.from('entities').select('id, name, display_label').eq('org_id', orgId).order('name'),
      supabase.from('org_members').select('role, user_id').eq('org_id', orgId),
    ]).then(([entitiesResult, rolesResult]) => {
      if (!entitiesResult.error) setEntities(entitiesResult.data || []);
      if (!rolesResult.error) {
        setAccessRoles([...new Set((rolesResult.data || []).map((row) => row.role).filter(Boolean))]);
        setAccessMembers((rolesResult.data || []).filter((row) => row.user_id));
      }
    });
  }, [orgId]);

  useEffect(() => {
    let mounted = true;
    async function loadViewer() {
      const { data: authData } = await supabase.auth.getUser();
      const userId = authData?.user?.id || null;
      if (!mounted) return;
      if (!orgId || !userId) {
        setCanManage(false);
        return;
      }
      const { data: membership } = await supabase
        .from('org_members')
        .select('role')
        .eq('org_id', orgId)
        .eq('user_id', userId)
        .maybeSingle();
      if (mounted) {
        setCanManage(['owner', 'admin'].includes(membership?.role || ''));
      }
    }
    loadViewer().catch(() => {
      if (mounted) setCanManage(false);
    });
    return () => {
      mounted = false;
    };
  }, [orgId]);

  async function loadProviders() {
    setLoading(true);
    setError(null);
    const { data, error: dbErr } = await supabase
      .from('org_email_providers')
      .select('id, provider_type, label, is_active, from_email, from_name, imap_host, imap_port, smtp_host, smtp_port, sender_allowlist, created_at, brand_entity_id, status, last_synced_at, last_error, sync_enabled, sync_interval_minutes, sender_blocklist, signature, reply_to, is_default_for_brand, access_mode, folder_sync')
      .order('created_at', { ascending: false });
    if (dbErr) setError(dbErr.message);
    else setProviders(data ?? []);
    setLoading(false);
  }

  function buildEmptyForm(providerType) {
    return {
      provider_type: providerType ?? 'gmail',
      label: '',
      from_email: '',
      from_name: '',
      is_active: false,
      brand_entity_id: '',
      status: 'needs_attention',
      reply_to: '',
      signature: '',
      sync_enabled: true,
      sync_interval_minutes: '1',
      sender_blocklist: [],
      is_default_for_brand: false,
      access_mode: 'filtered',
      folder_sync: { folders: ['Inbox', 'Sent'], auto_add_new: false, two_way: true },
      // OAuth fields (gmail / microsoft_graph)
      client_id: '',
      client_secret: '',
      refresh_token: '',
      pubsub_topic: '',
      tenant_id: '', // microsoft_graph only
      // IMAP fields
      imap_host: '',
      imap_port: String(DEFAULT_IMAP_PORT),
      imap_use_tls: true,
      // SMTP fields
      smtp_host: '',
      smtp_port: String(DEFAULT_SMTP_PORT),
      smtp_use_tls: true,
      // IMAP/SMTP auth
      username: '',
      password: '',
    };
  }

  function startNew() {
    if (!canManage) return;
    setForm(buildEmptyForm('gmail'));
    setEditing('new');
    setSetupStep(1);
    setError(null);
    setSuccess(null);
    setDetailSection('setup');
    setTestResult(null);
    setAccessDrafts({});
  }

  function startEdit(row) {
    const creds = {};
    setForm({
      provider_type: row.provider_type,
      label: row.label ?? '',
      from_email: row.from_email ?? '',
      from_name: row.from_name ?? '',
      is_active: row.is_active ?? false,
      client_id: creds.client_id ?? '',
      client_secret: '', // Never pre-fill secrets
      refresh_token: '',
      pubsub_topic: '',
      tenant_id: creds.tenant_id ?? '',
      imap_host: row.imap_host ?? '',
      imap_port: String(row.imap_port ?? DEFAULT_IMAP_PORT),
      imap_use_tls: row.imap_use_tls !== false,
      smtp_host: row.smtp_host ?? '',
      smtp_port: String(row.smtp_port ?? DEFAULT_SMTP_PORT),
      smtp_use_tls: row.smtp_use_tls !== false,
      username: creds.username ?? '',
      password: '',
      brand_entity_id: row.brand_entity_id ?? '',
      status: row.status ?? (row.is_active ? 'connected' : 'paused'),
      reply_to: row.reply_to ?? '',
      signature: row.signature ?? '',
      sync_enabled: row.sync_enabled !== false,
      sync_interval_minutes: String(row.sync_interval_minutes ?? 1),
      sender_blocklist: normalizeSenderBlocklist(row.sender_blocklist),
      is_default_for_brand: row.is_default_for_brand ?? false,
      access_mode: row.access_mode || 'filtered',
      folder_sync: row.folder_sync || { folders: [], auto_add_new: false, two_way: false },
    });
    setEditing(row.id);
    setSetupStep(1);
    setDetailSection('setup');
    setTestResult(null);
    supabase
      .from('org_email_provider_access')
      .select('role, staff_user_id, can_view, can_send')
      .eq('org_id', orgId)
      .eq('mailbox_id', row.id)
      .then(({ data }) => {
        const next = {};
        (data || []).forEach((access) => {
          const key = access.staff_user_id ? `user:${access.staff_user_id}` : `role:${access.role}`;
          next[key] = { role: access.role, staff_user_id: access.staff_user_id, can_view: access.can_view, can_send: access.can_send };
        });
        setAccessDrafts(next);
      });
    setError(null);
    setSuccess(null);
  }

  function cancelEdit() {
    setEditing(null);
    setError(null);
    setSuccess(null);
    setTestResult(null);
  }

  function setField(key, value) {
    setTestResult(null);
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      // When provider_type changes, reset the form to sane defaults.
      if (key === 'provider_type') return {
        ...buildEmptyForm(value),
        label: prev.label,
        from_email: prev.from_email,
        from_name: prev.from_name,
        brand_entity_id: prev.brand_entity_id,
        reply_to: prev.reply_to,
        signature: prev.signature,
        sync_enabled: prev.sync_enabled,
        sync_interval_minutes: prev.sync_interval_minutes,
        sender_blocklist: prev.sender_blocklist,
        is_default_for_brand: prev.is_default_for_brand,
      };
      return next;
    });
  }

  function buildCredentials() {
    const credentials = {};
    if (form.provider_type === 'gmail') {
      if (form.client_id) credentials.client_id = form.client_id;
      if (form.client_secret) credentials.client_secret = form.client_secret;
      if (form.refresh_token) credentials.refresh_token = form.refresh_token;
      if (form.pubsub_topic) credentials.pubsub_topic = form.pubsub_topic;
    } else if (form.provider_type === 'microsoft_graph') {
      if (form.tenant_id) credentials.tenant_id = form.tenant_id;
      if (form.client_id) credentials.client_id = form.client_id;
      if (form.client_secret) credentials.client_secret = form.client_secret;
    } else if (form.provider_type === 'imap_smtp') {
      if (form.username) credentials.username = form.username;
      if (form.password) credentials.password = form.password;
    }
    return credentials;
  }

  async function testConnection(providerId = editing === 'new' ? null : editing) {
    if (!canManage || !orgId) return;
    setTesting(true);
    setError(null);
    setTestResult(null);
    const { data, error: testError } = await supabase.functions.invoke('email-provider-test', {
      body: {
        orgId,
        providerId,
        settings: {
          provider_type: form.provider_type,
          from_email: form.from_email,
          imap_host: form.imap_host,
          imap_port: Number(form.imap_port) || DEFAULT_IMAP_PORT,
          smtp_host: form.smtp_host,
          smtp_port: Number(form.smtp_port) || DEFAULT_SMTP_PORT,
          tenant_id: form.tenant_id,
          client_id: form.client_id,
        },
        credentials: buildCredentials(),
      },
    });
    const result = testError
      ? { ok: false, error: testError.message || 'The connection test failed.' }
      : data;
    setTestResult(result);
    if (result?.ok) setSuccess(result.message || 'The connection test passed.');
    else setError(result?.error || 'The connection test failed.');
    setTesting(false);
    return result;
  }

  async function syncNow() {
    if (!canManage || !orgId) return;
    setSaving(true);
    setError(null);
    const { data, error: syncError } = await supabase.functions.invoke('email-provider-admin', {
      body: { action: 'sync', orgId, providerIds: selectedMailboxIds },
    });
    if (syncError || !data?.ok) setError(syncError?.message || data?.error || 'Could not sync mailboxes.');
    else {
      setSuccess('Mailbox sync requested.');
      await loadProviders();
    }
    setSaving(false);
  }

  async function exportMailboxes() {
    const csvCell = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const rows = [
      ['Email', 'Display name', 'Brand', 'Provider', 'Access mode', 'Status', 'Last sync'],
      ...providers.map((row) => [
        row.from_email,
        row.from_name,
        entities.find((entity) => entity.id === row.brand_entity_id)?.display_label || '',
        PROVIDER_LABELS[row.provider_type] || row.provider_type,
        row.access_mode || 'filtered',
        mailboxStatusLabel(row.status, row.is_active),
        row.last_synced_at || '',
      ]),
    ];
    const blob = new Blob([rows.map((row) => row.map(csvCell).join(',')).join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'email-mailboxes.csv';
    anchor.click();
    URL.revokeObjectURL(url);
  }

  async function advanceSetup() {
    if (setupStep === 1) {
      setDetailSection('access');
      setSetupStep(2);
    } else if (setupStep === 2) {
      setDetailSection(form.access_mode === 'full' ? 'folders' : 'sender-rules');
      setSetupStep(3);
    } else if (setupStep === 3) {
      setDetailSection('access');
      setSetupStep(4);
    } else {
      await save();
    }
  }

  async function suggestProvider() {
    if (!form.from_email.trim()) {
      setError('Enter an email address before looking up its provider.');
      return;
    }
    setDiscovering(true);
    setError(null);
    const { data, error: discoveryError } = await supabase.functions.invoke('email-provider-discover', {
      body: { email: form.from_email },
    });
    const suggestion = suggestEmailProvider(form.from_email);
    const providerType = data?.providerType || suggestion?.provider_type;
    if (!providerType) {
      setError(discoveryError?.message || data?.error || 'We could not identify the provider. Choose it below.');
    } else {
      setField('provider_type', providerType);
      setForm((current) => ({
        ...current,
        ...(suggestion?.provider_type === providerType
          ? {
              imap_host: suggestion.imap_host || current.imap_host,
              imap_port: String(suggestion.imap_port || current.imap_port),
              smtp_host: suggestion.smtp_host || current.smtp_host,
              smtp_port: String(suggestion.smtp_port || current.smtp_port),
            }
          : {}),
      }));
      setSuccess(data?.providerType
        ? `Mail domain suggests ${PROVIDER_LABELS[providerType]}.`
        : `Suggested ${PROVIDER_LABELS[providerType]} from the email domain; verify this choice.`);
    }
    setDiscovering(false);
  }

  async function save() {
    if (!canManage) return;
    if (!testResult?.ok) {
      const warning = testResult?.ok === false
        ? `The last connection test failed: ${testResult.error || 'unknown error'}.\n\nSave this mailbox anyway as “Needs attention”?`
        : 'This mailbox has not passed a connection test. Save it anyway as “Needs attention”?';
      if (!window.confirm(warning)) return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);

    const senderBlocklist = normalizeSenderBlocklist(form.sender_blocklist);
    const record = {
      provider_type: form.provider_type,
      label: form.label || null,
      from_email: form.from_email || null,
      from_name: form.from_name || null,
      is_active: form.is_active,
      brand_entity_id: form.brand_entity_id || null,
      status: form.is_active && form.sync_enabled
        ? (testResult?.ok ? 'connected' : 'needs_attention')
        : 'paused',
      sync_enabled: form.sync_enabled,
      sync_interval_minutes: Number(form.sync_interval_minutes) || 1,
      sender_blocklist: senderBlocklist,
      signature: form.signature || null,
      reply_to: form.reply_to || null,
      is_default_for_brand: Boolean(form.brand_entity_id && form.is_default_for_brand),
      access_mode: form.access_mode,
      folder_sync: { ...form.folder_sync, two_way: true, auto_add_new: false },
    };

    if (form.provider_type === 'imap_smtp') {
      record.imap_host = form.imap_host || null;
      record.imap_port = form.imap_port ? Number(form.imap_port) : null;
      record.imap_use_tls = form.imap_use_tls;
      record.smtp_host = form.smtp_host || null;
      record.smtp_port = form.smtp_port ? Number(form.smtp_port) : null;
      record.smtp_use_tls = form.smtp_use_tls;
    }

    const { data, error: saveError } = await supabase.functions.invoke('email-provider-admin', {
      body: {
        action: 'save',
        orgId,
        providerId: editing === 'new' ? null : editing,
        provider: { ...record, credentials: buildCredentials() },
      },
    });
    if (saveError || !data?.ok) {
      setError(saveError?.message || data?.error || 'Could not save the mailbox.');
    } else {
      setSuccess(editing === 'new' ? 'Mailbox added.' : 'Mailbox updated.');
      setEditing(null);
      setTestResult(null);
      await loadProviders();
    }
    setSaving(false);
  }

  async function remove(id) {
    if (!canManage) return;
    if (!window.confirm('Remove this email provider? This cannot be undone.')) return;
    const { data, error: dbErr } = await supabase.functions.invoke('email-provider-admin', {
      body: { action: 'delete', orgId, providerId: id },
    });
    if (dbErr || !data?.ok) setError(dbErr?.message || data?.error || 'Could not remove the mailbox.');
    else await loadProviders();
  }

  async function setActive(id) {
    if (!canManage) return;
    setSaving(true);
    setError(null);
    const { error: activeError } = await supabase
      .from('org_email_providers')
      .update({ is_active: true })
      .eq('id', id);
    if (activeError) setError(activeError.message);
    else await loadProviders();
    setSaving(false);
  }

  async function saveMailboxAccess(subjectType, subjectId, permission, allowed) {
    if (!canManage || !orgId || editing === 'new') return;
    const key = `${subjectType}:${subjectId}`;
    const current = accessDrafts[key] || { can_view: false, can_send: false };
    const next = { ...current, [permission]: allowed };
    if (permission === 'can_send' && allowed) next.can_view = true;
    if (permission === 'can_view' && !allowed) next.can_send = false;
    let existingQuery = supabase
      .from('org_email_provider_access')
      .select('id')
      .eq('org_id', orgId)
      .eq('mailbox_id', editing);
    existingQuery = subjectType === 'role'
      ? existingQuery.eq('role', subjectId).is('staff_user_id', null)
      : existingQuery.is('role', null).eq('staff_user_id', subjectId);
    const existing = await existingQuery.maybeSingle();
    if (existing.error) {
      setError(existing.error.message);
      return;
    }
    let result;
    if (!next.can_view && !next.can_send) {
      let deleteQuery = supabase.from('org_email_provider_access')
        .delete().eq('org_id', orgId).eq('mailbox_id', editing);
      deleteQuery = subjectType === 'role'
        ? deleteQuery.eq('role', subjectId).is('staff_user_id', null)
        : deleteQuery.is('role', null).eq('staff_user_id', subjectId);
      result = await deleteQuery;
    } else if (existing.data?.id) {
      const permissionPatch = { can_view: next.can_view, can_send: next.can_send };
      result = await supabase.from('org_email_provider_access')
        .update(permissionPatch).eq('id', existing.data.id).eq('org_id', orgId);
    } else {
      result = await supabase.from('org_email_provider_access').insert({
        org_id: orgId,
        mailbox_id: editing,
        ...(subjectType === 'role' ? { role: subjectId } : { staff_user_id: subjectId }),
        ...next,
      });
    }
    if (result?.error) setError(result.error.message);
    else {
      setAccessDrafts((previous) => ({ ...previous, [key]: { ...next, role: subjectType === 'role' ? subjectId : null, staff_user_id: subjectType === 'user' ? subjectId : null } }));
      setSuccess('Mailbox access updated.');
    }
  }

  function setAllowlistDraft(providerId, value) {
    setAllowlistDrafts((prev) => ({ ...prev, [providerId]: value }));
  }

  async function loadCurrentAllowlist(providerId) {
    const { data, error: dbErr } = await supabase
      .from('org_email_providers')
      .select('sender_allowlist, updated_at')
      .eq('id', providerId)
      .maybeSingle();
    if (dbErr) throw dbErr;
    return {
      entries: normalizeSenderAllowlist(data?.sender_allowlist),
      updatedAt: data?.updated_at ?? null,
    };
  }

  async function addAllowlistEntry(row) {
    if (!canManage) return;
    const draft = allowlistDrafts[row.id] ?? '';
    const normalized = normalizeEmailAllowlistEntry(draft);
    setError(null);
    setSuccess(null);
    if (!normalized) {
      setError('Enter an email address or @domain.com before adding it to Allowed senders.');
      return;
    }
    if (!isValidEmailAllowlistEntry(normalized)) {
      setError('Allowed senders must be a full email address like name@example.com or a whole-domain rule like @example.com.');
      return;
    }
    setSaving(true);
    try {
      const currentAllowlist = await loadCurrentAllowlist(row.id);
      const existingEntries = currentAllowlist.entries;
      if (senderAllowlistIncludes(existingEntries, normalized)) {
        setError('That sender is already in the allowlist.');
      } else {
        const { data: updatedRows, error: dbErr } = await supabase
          .from('org_email_providers')
          .update({ sender_allowlist: [...existingEntries, normalized] })
          .eq('id', row.id)
          .eq('updated_at', currentAllowlist.updatedAt)
          .select('id');
        if (dbErr) throw dbErr;
        if (!updatedRows?.length) {
          setError('Allowed senders changed in another session. Reload this page and try again.');
          return;
        }
        setSuccess('Allowed senders updated.');
        setAllowlistDraft(row.id, '');
        await loadProviders();
      }
    } catch (dbErr) {
      setError(dbErr.message);
    } finally {
      setSaving(false);
    }
  }

  function requestAllowlistRemoval(row, entry) {
    if (!canManage) return;
    setPendingAllowlistRemoval({
      providerId: row.id,
      providerLabel: row.label || PROVIDER_LABELS[row.provider_type] || 'Email provider',
      entry,
    });
    setError(null);
    setSuccess(null);
  }

  function closeAllowlistRemovalDialog() {
    if (saving) return;
    setPendingAllowlistRemoval(null);
  }

  async function confirmAllowlistRemoval() {
    if (!pendingAllowlistRemoval) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const currentAllowlist = await loadCurrentAllowlist(pendingAllowlistRemoval.providerId);
      const nextAllowlist = currentAllowlist.entries
        .filter((entry) => entry !== pendingAllowlistRemoval.entry);
      const { data: updatedRows, error: dbErr } = await supabase
        .from('org_email_providers')
        .update({ sender_allowlist: nextAllowlist })
        .eq('id', pendingAllowlistRemoval.providerId)
        .eq('updated_at', currentAllowlist.updatedAt)
        .select('id');
      if (dbErr) throw dbErr;
      if (!updatedRows?.length) {
        setError('Allowed senders changed in another session. Reload this page and try again.');
        return;
      }
      setSuccess('Allowed senders updated.');
      setPendingAllowlistRemoval(null);
      await loadProviders();
    } catch (dbErr) {
      setError(dbErr.message);
    } finally {
      setSaving(false);
    }
  }

  // -------------------------------------------------------------------------
  // Render helpers
  // -------------------------------------------------------------------------

  function renderOAuthFields() {
    if (form.provider_type === 'gmail') {
      return (
        <>
          <p className="text-xs text-gray-500 mb-3">
            Connect with a Google OAuth client that has the gmail.modify scope for reading, sending, flags and Trash. Credentials are stored server-side.
          </p>
          <Field label="OAuth Client ID" value={form.client_id} onChange={(v) => setField('client_id', v)} placeholder="Your Google OAuth client ID" />
          <Field label="OAuth Client Secret" value={form.client_secret} onChange={(v) => setField('client_secret', v)} placeholder="Leave blank to keep existing" type="password" />
          <Field label="Pub/Sub topic for instant Gmail sync" value={form.pubsub_topic || ''} onChange={(v) => setField('pubsub_topic', v)} placeholder="projects/your-project/topics/mail (blank keeps existing)" />
          <Field label="OAuth Refresh Token" value={form.refresh_token} onChange={(v) => setField('refresh_token', v)} placeholder="Leave blank to keep existing" type="password" />
        </>
      );
    }
    if (form.provider_type === 'microsoft_graph') {
      return (
        <>
          <p className="text-xs text-gray-500 mb-3">
            Use Microsoft Graph application permissions Mail.Read (inbound email and Sent Items lookup) and Mail.Send (replies and new email). Mail.ReadWrite is not required. Credentials are stored server-side.
          </p>
          <Field label="Azure Tenant ID" value={form.tenant_id} onChange={(v) => setField('tenant_id', v)} placeholder="Your Azure AD tenant ID" />
          <Field label="OAuth Client ID" value={form.client_id} onChange={(v) => setField('client_id', v)} placeholder="Your Azure AD app client ID" />
          <Field label="OAuth Client Secret" value={form.client_secret} onChange={(v) => setField('client_secret', v)} placeholder="Leave blank to keep existing" type="password" />
        </>
      );
    }
    return null;
  }

  function renderImapSmtpFields() {
    if (form.provider_type !== 'imap_smtp') return null;
    return (
      <>
        <p className="text-xs text-gray-500 mb-3">
          Generic IMAP (inbound, port 993 implicit TLS) + SMTP (outbound, port 465 implicit TLS).
          Polling-based — choose how often to check for new messages below.
        </p>
        <h4 className="text-sm font-medium text-gray-700 mt-4 mb-2">IMAP (Inbound)</h4>
        <Field label="IMAP Host" value={form.imap_host} onChange={(v) => setField('imap_host', v)} placeholder="imap.example.com" />
        <Field label="IMAP Port" value={form.imap_port} onChange={(v) => setField('imap_port', v)} placeholder="993" type="number" />
        <h4 className="text-sm font-medium text-gray-700 mt-4 mb-2">SMTP (Outbound)</h4>
        <Field label="SMTP Host" value={form.smtp_host} onChange={(v) => setField('smtp_host', v)} placeholder="smtp.example.com" />
        <Field label="SMTP Port" value={form.smtp_port} onChange={(v) => setField('smtp_port', v)} placeholder="465" type="number" />
        <h4 className="text-sm font-medium text-gray-700 mt-4 mb-2">Authentication</h4>
        <Field label="Username / Email" value={form.username} onChange={(v) => setField('username', v)} placeholder="mailbox@example.com" />
        <Field label="Password" value={form.password} onChange={(v) => setField('password', v)} placeholder="Leave blank to keep existing" type="password" />
      </>
    );
  }

  // -------------------------------------------------------------------------
  // Main render
  // -------------------------------------------------------------------------

  if (loading) {
    return <div className="p-6 text-gray-500">Loading email channel settings…</div>;
  }

  const renderMailboxFlyout = () => (
      <div className="fixed inset-0 z-50 flex justify-end bg-black/55">
        <aside className={`h-full w-full max-w-[620px] overflow-y-auto shadow-2xl ${theme === 'dark' ? 'bg-[#0F151F] text-gray-100' : 'bg-white text-gray-900'}`} aria-label="Mailbox details">
        <div className={`sticky top-0 z-10 border-b px-6 py-4 ${theme === 'dark' ? 'border-[#232B38] bg-[#0F151F]' : 'border-gray-200 bg-white'}`}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="flex items-center gap-2 text-xl font-semibold"><span aria-hidden="true">✉</span>{form.from_email || (editing === 'new' ? 'Add mailbox' : 'Mailbox details')}</h2>
              <p className="mt-1 text-sm text-gray-500">{PROVIDER_LABELS[form.provider_type]} · {entities.find((entity) => entity.id === form.brand_entity_id)?.display_label || 'Organization-wide'}</p>
              <div className="mt-2 flex gap-2">
                <span className="rounded-full border px-2 py-0.5 text-xs">{mailboxStatusLabel(form.is_active && form.sync_enabled ? form.status : 'paused', form.is_active)}</span>
                <span className="rounded-full border border-[#03B7C9] px-2 py-0.5 text-xs text-[#03B7C9]">{form.access_mode === 'full' ? 'Full mailbox' : 'Bookings only'}</span>
              </div>
            </div>
            <button type="button" onClick={cancelEdit} aria-label="Close mailbox details" className="rounded-full border px-3 py-1 text-lg">×</button>
          </div>
        </div>
        <div className="p-6">
        <h2 className="text-xl font-semibold mb-1">{editing === 'new' ? 'Add mailbox' : 'Mailbox settings'}</h2>
        <p className="text-sm text-gray-500 mb-6">
          Choose what this mailbox shares with IntelAgent and who can use it.
        </p>
        {editing === 'new' && (
          <div className="mb-5" aria-label={`Setup step ${setupStep} of 4`}>
            <div className="mb-2 flex justify-between text-xs text-gray-500"><span>Step {setupStep} of 4</span><span>{['Connect', 'Choose access mode', 'Folders or senders', 'Access and test'][setupStep - 1]}</span></div>
            <div className="h-1.5 overflow-hidden rounded-full bg-gray-200"><div className="h-full bg-[#03B7C9] transition-all" style={{ width: `${setupStep * 25}%` }} /></div>
          </div>
        )}

        {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded px-4 py-3 mb-4">{error}</div>}
        {success && <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded px-4 py-3 mb-4">{success}</div>}

        <div className="mb-5 flex gap-2 border-b border-gray-200 pb-3" role="tablist" aria-label="Mailbox settings">
          {['general', 'setup', 'access', 'sender-rules', 'folders', 'health'].map((section) => (
            <button
              key={section}
              type="button"
              role="tab"
              aria-selected={detailSection === section}
              onClick={() => {
                setDetailSection(section);
                if (editing === 'new') {
                  setSetupStep(section === 'setup' ? 1 : ['folders', 'sender-rules'].includes(section) ? 3 : 2);
                }
              }}
              className={'rounded-full border px-4 py-1.5 text-sm ' +
                (detailSection === section ? 'border-green-600 text-green-700' : 'border-gray-300 text-gray-600 hover:border-gray-500')}
            >
              {{ general: 'General', setup: 'Setup', access: 'Access', 'sender-rules': 'Sender rules', folders: 'Folders', health: 'Health' }[section]}
            </button>
          ))}
        </div>

        {detailSection === 'general' ? (
          <section className="space-y-4">
            <div><h3 className="font-semibold">Mailbox overview</h3><p className="mt-1 text-sm text-gray-500">Review the address, brand, provider, and visibility mode for this mailbox.</p></div>
            <p className="rounded-lg border p-4 text-sm"><strong>Address:</strong> {form.from_email || 'Not set'}<br /><strong>Brand:</strong> {entities.find((entity) => entity.id === form.brand_entity_id)?.display_label || 'Organization-wide'}<br /><strong>What IntelAgent sees:</strong> {form.access_mode === 'full' ? 'Full mailbox' : 'Bookings only'}</p>
          </section>
        ) : detailSection === 'setup' ? (
          <>
            <Field label="Email address" value={form.from_email} onChange={(v) => setField('from_email', v)} onBlur={suggestProvider} placeholder="dispatch@yourcompany.com" />
            <p className="mb-3 text-xs text-gray-500">{form.from_email.includes('@') ? `Provider detected from ${form.from_email.split('@')[1]}. ${form.status === 'connected' ? 'Signed in · renews automatically.' : 'Continue to connect this mailbox.'}` : 'Enter your mailbox address to detect its provider.'}</p>
            <button
              type="button"
              onClick={suggestProvider}
              disabled={discovering}
              className="ia-hover-pulse mb-5 rounded-full border border-green-600 px-4 py-1.5 text-sm text-green-700 disabled:opacity-50"
            >
              {discovering ? 'Checking domain…' : 'Suggest provider from domain'}
            </button>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Provider</label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {Object.entries(PROVIDER_LABELS).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setField('provider_type', key)}
                    className={'rounded-lg border px-3 py-2 text-left text-sm ' +
                      (form.provider_type === key ? 'border-green-600 text-green-700' : 'border-gray-300 text-gray-700 hover:border-gray-500')}
                  >
                    <div className="font-medium">{label}</div>
                    <div className="mt-0.5 text-xs leading-snug text-gray-500">{PROVIDER_DESCRIPTIONS[key]}</div>
                  </button>
                ))}
              </div>
            </div>

            <Field label="Display name / From name" value={form.from_name} onChange={(v) => setField('from_name', v)} placeholder="Dispatch Team" />
            <Field label="Mailbox label (optional)" value={form.label} onChange={(v) => setField('label', v)} placeholder="Reservations" />
            <div className="mb-4">
              <label htmlFor="mailbox-brand" className="mb-1 block text-sm font-medium text-gray-700">Brand</label>
              <select id="mailbox-brand" value={form.brand_entity_id} onChange={(event) => setField('brand_entity_id', event.target.value)} className="w-full rounded border border-gray-300 px-3 py-2 text-sm">
                <option value="">Organization-wide</option>
                {entities.map((entity) => <option key={entity.id} value={entity.id}>{entity.display_label || entity.name}</option>)}
              </select>
            </div>
            <Field label="Reply-to (optional)" value={form.reply_to} onChange={(v) => setField('reply_to', v)} placeholder="support@example.com" />
            <label className="mb-4 block text-sm font-medium text-gray-700">
              Signature
              <textarea value={form.signature} onChange={(event) => setField('signature', event.target.value)} rows={3} className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm" placeholder="Message signature" />
            </label>

            {renderOAuthFields()}
            {renderImapSmtpFields()}

            <div className="my-5 space-y-3 rounded-xl border border-gray-200 p-4">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={form.sync_enabled} onChange={(event) => setField('sync_enabled', event.target.checked)} />
                Check this mailbox for new messages
              </label>
              {form.provider_type === 'imap_smtp' ? (
                <div className="max-w-xs">
                  <label htmlFor="mailbox-sync-interval" className="mb-1 block text-sm font-medium text-gray-700">Check every</label>
                  <select id="mailbox-sync-interval" value={form.sync_interval_minutes} onChange={(event) => setField('sync_interval_minutes', event.target.value)} className="w-full rounded border border-gray-300 px-3 py-2 text-sm">
                    {MAILBOX_CHECK_MINUTES.map((minutes) => <option key={minutes} value={minutes}>{minutes} minute{minutes === 1 ? '' : 's'}</option>)}
                  </select>
                </div>
              ) : (
                <p data-testid="mailbox-instant-note" className="text-sm text-gray-600">
                  {form.provider_type === 'gmail' ? 'Gmail' : 'Microsoft'} mailboxes are instant: new mail arrives as it is sent, so there is no check interval.
                </p>
              )}
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={form.is_default_for_brand} disabled={!form.brand_entity_id} onChange={(event) => setField('is_default_for_brand', event.target.checked)} />
                Default sender for this brand
              </label>
              {!form.brand_entity_id && form.is_default_for_brand && <p className="text-xs text-amber-700">Choose a brand to save this mailbox as its default sender.</p>}
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={form.is_active} onChange={(event) => setField('is_active', event.target.checked)} />
                Enable this mailbox
              </label>
            </div>

            <div className="mb-5 rounded-xl border border-gray-200 p-4">
              <button
                type="button"
                onClick={testConnection}
                disabled={testing || !canManage}
                className="ia-hover-pulse rounded-full border border-green-600 px-4 py-2 text-sm font-medium text-green-700 disabled:opacity-50"
              >
                {testing ? 'Testing read and send…' : 'Test connection'}
              </button>
              {testResult && (
                <p role="status" className={'mt-3 text-sm ' + (testResult.ok ? 'text-green-700' : 'text-red-700')}>
                  {testResult.message || testResult.error}
                </p>
              )}
              <p className="mt-2 text-xs text-gray-500">The test checks inbox access and send permission without sending a test email. Failed or untested mailboxes require confirmation before saving.</p>
            </div>
          </>
        ) : detailSection === 'access' ? (
          <div className="rounded-xl border border-gray-200 p-4">
            <h3 className="text-base font-medium">What IntelAgent can see</h3>
            <p className="mt-1 text-sm text-gray-500">
              Choose whether to bring in approved booking senders only, or the folders you select from the full mailbox.
            </p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {[
                ['filtered', 'Bookings only', 'Only approved senders become conversations; nothing else is read.', 'Private by default'],
                ['full', 'Full mailbox', 'Selected folders appear in Communications › Email, like Outlook.', 'Only you can read it unless shared'],
              ].map(([value, label, description, chip]) => (
                <button key={value} type="button" onClick={() => setField('access_mode', value)} aria-pressed={form.access_mode === value}
                  className={`rounded-lg border p-4 text-left ${form.access_mode === value ? 'border-[#03B7C9] bg-[#03B7C9]/5' : 'border-gray-300'}`}>
                  <span className="block font-semibold">{label}</span><span className="mt-1 block text-xs text-gray-500">{description}</span>
                  <span className="mt-2 inline-block rounded-full border px-2 py-0.5 text-[11px]">{chip}</span>
                </button>
              ))}
            </div>
            <h3 className="mt-6 text-base font-medium text-gray-800">Who can use this mailbox</h3>
            <p className="mt-1 text-sm text-gray-500">Personal mailboxes remain private to their owner unless shared. Mailboxes start with owner and admin access; add other roles or people as needed. Sending requires read access to the mailbox and conversation.</p>
            {editing === 'new' ? (
              <p className="mt-4 text-sm text-gray-500">Save this mailbox first to add people or roles.</p>
            ) : (
              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead><tr className="border-b text-xs text-gray-500"><th className="py-2">Person or role</th><th className="py-2">Can read</th><th className="py-2">Can send</th><th className="py-2">Default sender</th></tr></thead>
                  <tbody className="divide-y">
                  {[...accessRoles.map((role) => ({ key: `role:${role}`, label: role, type: 'role', id: role })),
                    ...Object.entries(accessDrafts).filter(([key]) => key.startsWith('user:')).map(([key, access]) => {
                      const member = accessMembers.find((item) => item.user_id === access.staff_user_id) || {};
                      return { key, label: member.full_name || `Staff ${String(access.staff_user_id || '').slice(0, 8)}`, type: 'user', id: access.staff_user_id };
                    })].map((entry) => {
                  const access = accessDrafts[entry.key] || { can_view: false, can_send: false };
                  return (
                    <tr key={entry.key}>
                      <td className="py-3 font-medium capitalize">{entry.label}</td>
                      {['can_view', 'can_send'].map((permission) => (
                        <td key={permission} className="py-3">
                          <label className="flex items-center gap-2 text-sm text-gray-600">
                            <input type="checkbox" checked={Boolean(access[permission])} disabled={!canManage || (permission === 'can_view' && access.can_send)}
                              onChange={(event) => saveMailboxAccess(entry.type, entry.id, permission, event.target.checked)} />
                            <span className="sr-only">{permission === 'can_view' ? 'Can read' : 'Can send'} for {entry.label}</span>
                          </label>
                        </td>
                      ))}
                      <td className="py-3">
                        <span className={`rounded-full border px-2 py-1 text-xs ${form.is_default_for_brand ? 'border-green-500 text-green-600' : 'border-gray-300 text-gray-500'}`}>
                          {form.is_default_for_brand ? '✓ Yes' : 'No'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                  </tbody>
                </table>
                {canManage && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    <select aria-label="Add person or role" value={newAccessSubject} onChange={(event) => setNewAccessSubject(event.target.value)} className="rounded border px-3 py-2 text-sm">
                      <option value="">Choose person or role</option>
                      {accessRoles.map((role) => <option key={`role:${role}`} value={`role:${role}`}>Role: {role}</option>)}
                      {accessMembers.map((member) => <option key={`user:${member.user_id}`} value={`user:${member.user_id}`}>Person: {member.full_name || member.user_id}</option>)}
                    </select>
                    <button type="button" disabled={!newAccessSubject} onClick={() => {
                      const [type, id] = newAccessSubject.split(':');
                      saveMailboxAccess(type, id, 'can_view', true);
                    }} className="rounded-full border border-[#03B7C9] px-4 py-2 text-sm text-[#03B7C9]">+ Add person or role</button>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : detailSection === 'sender-rules' ? (
          <MailboxSenderRules
            row={providers.find((provider) => provider.id === editing)}
            canManage={canManage}
            saving={saving}
            allowlistDraft={allowlistDrafts[editing] ?? ''}
            blockedSenders={normalizeSenderBlocklist(form.sender_blocklist)}
            onAllowlistDraftChange={(value) => setAllowlistDraft(editing, value)}
            onAddAllowlist={() => addAllowlistEntry(providers.find((provider) => provider.id === editing))}
            onRemoveAllowlist={(entry) => requestAllowlistRemoval(providers.find((provider) => provider.id === editing), entry)}
            onBlocklistChange={(value) => setField('sender_blocklist', value.split('\n'))}
          />
        ) : detailSection === 'folders' ? (
          <MailboxFoldersPanel
            enabled={true}
            folderSync={form.folder_sync}
            onChange={(folder_sync) => setField('folder_sync', folder_sync)}
          />
        ) : (
          <section className="space-y-3">
            <h3 className="text-base font-semibold">Sync health</h3>
            <p className="text-sm text-gray-600">Last successful sync: {providers.find((provider) => provider.id === editing)?.last_synced_at || 'Not synced yet'}</p>
            <p className="text-sm text-gray-600">Last error: {providers.find((provider) => provider.id === editing)?.last_error || 'No current errors'}</p>
            <button type="button" onClick={syncNow} disabled={saving || !canManage} className="rounded-full border border-[#03B7C9] px-4 py-2 text-sm text-[#03B7C9]">{saving ? 'Syncing…' : 'Sync now'}</button>
            {testResult && <p role="status" className="text-sm">{testResult.message || testResult.error}</p>}
          </section>
        )}
        <div className={`sticky bottom-0 mt-6 flex flex-wrap items-center justify-between gap-3 border-t py-4 ${theme === 'dark' ? 'border-[#232B38] bg-[#0F151F]' : 'border-gray-200 bg-white'}`}>
          <div className="flex gap-2">
            <button type="button" onClick={editing === 'new' ? advanceSetup : save} disabled={saving || !canManage} className="ia-hover-pulse rounded-full bg-[#03B7C9] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
              {saving ? 'Saving…' : editing === 'new' && setupStep < 4 ? 'Continue' : 'Save changes'}
            </button>
            <button type="button" onClick={cancelEdit} disabled={saving} className="rounded-full border border-gray-300 px-4 py-2 text-sm">Cancel</button>
          </div>
          <button type="button" onClick={testConnection} disabled={testing || !canManage} className="rounded-full border border-[#03B7C9] px-4 py-2 text-sm text-[#03B7C9] disabled:opacity-50">
            {testing ? 'Testing…' : 'Test connection'}
          </button>
        </div>
        </div>
        </aside>
      </div>
  );

  return (
    <div className="relative w-full p-4">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border-y border-gray-200 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={startNew} disabled={!canManage} className="ia-hover-pulse rounded-full border border-[#03B7C9] px-4 py-2 text-sm font-medium text-[#03B7C9] disabled:opacity-50">+ Add mailbox</button>
          <button type="button" disabled={!canManage || providers.length === 0} onClick={syncNow} className="ia-hover-pulse rounded-full border border-gray-300 px-4 py-2 text-sm">Sync now</button>
          <button type="button" disabled={testing || providers.length === 0} onClick={() => testConnection(selectedMailboxIds[0] || providers[0]?.id)} className="ia-hover-pulse rounded-full border border-gray-300 px-4 py-2 text-sm">Test connection</button>
          <button type="button" onClick={exportMailboxes} disabled={providers.length === 0} className="ia-hover-pulse rounded-full border border-gray-300 px-4 py-2 text-sm">Export</button>
        </div>
        <span className="text-sm text-gray-500">{providers.length} mailbox{providers.length === 1 ? '' : 'es'} · {providers.filter((row) => row.status === 'needs_attention').length} need attention</span>
      </div>

      {!canManage && (
        <div className="mb-4 rounded-lg border border-gray-700 bg-gray-900/60 px-4 py-3 text-sm text-gray-300">
          You can review mailboxes here, but only organization owners and admins can change mailbox settings.
        </div>
      )}

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded px-4 py-3 mb-4">{error}</div>}
      {success && <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded px-4 py-3 mb-4">{success}</div>}

      {providers.length === 0 ? (
        <div className="border border-dashed border-gray-300 rounded-lg p-8 text-center text-gray-500 text-sm">
          {canManage
            ? <>No mailboxes configured yet. Click <strong>+ Add mailbox</strong> to set one up.</>
            : 'No mailboxes are configured for this organization yet.'}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-[#232B38]">
          <table className="min-w-full border-collapse text-left text-sm">
            <thead className="bg-[#0F151F] text-xs uppercase tracking-wide text-gray-400">
              <tr>
                <th className="px-3 py-3"><label><input aria-label="Select all mailboxes" type="checkbox" checked={providers.length > 0 && selectedMailboxIds.length === providers.length} onChange={(event) => setSelectedMailboxIds(event.target.checked ? providers.map((row) => row.id) : [])} /></label></th>
                {['Mailbox', 'Brand', 'Provider', 'What IntelAgent sees', 'Status', 'Last sync', 'Who has access'].map((heading) => <th key={heading} className="px-3 py-3 font-semibold">{heading}</th>)}
              </tr>
            </thead>
            <tbody>
              {providers.map((row) => {
                const brand = entities.find((entity) => entity.id === row.brand_entity_id);
                const status = !row.is_active || row.sync_enabled === false || row.status === 'paused'
                  ? { label: 'Paused', cls: 'border-gray-500 text-gray-400' }
                  : row.status === 'connected'
                    ? { label: 'Connected', cls: 'border-green-500 text-green-500' }
                    : { label: 'Needs attention', cls: 'border-[#EAB308] text-[#EAB308]' };
                const when = row.last_synced_at ? formatMailboxAge(row.last_synced_at) : (row.last_error || 'Not synced yet');
                return (
                  <tr key={row.id} onClick={() => { setSelectedMailboxIds((ids) => ids.includes(row.id) ? ids : [...ids, row.id]); startEdit(row); }} className="cursor-pointer border-t border-[#232B38] hover:bg-[#03B7C9]/5">
                    <td className="px-3 py-3" onClick={(event) => event.stopPropagation()}><label><input type="checkbox" aria-label={`Select ${row.from_email || row.label || 'mailbox'}`} checked={selectedMailboxIds.includes(row.id)} onChange={(event) => setSelectedMailboxIds((ids) => event.target.checked ? [...ids, row.id] : ids.filter((id) => id !== row.id))} /></label></td>
                    <td className="px-3 py-3"><strong className="block">{row.from_email || row.label || 'Unnamed mailbox'}</strong><span className="text-xs text-gray-500">{row.from_name || row.label || 'No display name'}</span></td>
                    <td className="px-3 py-3">{brand?.display_label || brand?.name || '—'}</td>
                    <td className="px-3 py-3"><ProviderBadge providerType={row.provider_type} /></td>
                    <td className="px-3 py-3"><span className={`rounded-full border px-2 py-1 text-xs ${row.access_mode === 'full' ? 'border-green-500 text-green-500' : 'border-[#03B7C9] text-[#03B7C9]'}`}>{row.access_mode === 'full' ? 'Full mailbox' : 'Bookings only'}</span></td>
                    <td className="px-3 py-3"><span className={`rounded-full border px-2 py-1 text-xs ${status.cls}`}>{status.label}</span></td>
                    <td className="px-3 py-3 text-xs text-gray-500">{when}</td>
                    <td className="px-3 py-3 text-xs text-gray-500">{row.is_default_for_brand ? 'Owners, admins · default sender' : 'Owners + admins'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-gray-400 mt-6">
        Note: Email security hardening (SPF/DKIM/DMARC sender verification, phishing detection) is
        deferred to Phase 2 (BL-178) and is not active here.
      </p>
      {pendingAllowlistRemoval && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Remove allowed sender confirmation"
            className="w-full max-w-md rounded-2xl border border-gray-700 bg-gray-900 p-5 text-gray-100 shadow-2xl"
          >
            <h3 className="text-lg font-semibold">Remove allowed sender?</h3>
            <p className="mt-2 text-sm text-gray-300">
              {pendingAllowlistRemoval.entry} will stop being part of the allowlist for {pendingAllowlistRemoval.providerLabel}.
            </p>
            <p className="mt-2 text-sm text-gray-400">
              Once removed, mail from this sender will only be pulled into Communications if another allowlist rule still matches it.
            </p>
            <div className="mt-5 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={closeAllowlistRemovalDialog}
                className="rounded border border-gray-600 px-4 py-2 text-sm font-medium text-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmAllowlistRemoval}
                disabled={saving}
                className="rounded border border-red-500/60 px-4 py-2 text-sm font-medium text-red-300 disabled:opacity-50"
              >
                {saving ? 'Removing…' : 'Remove sender'}
              </button>
            </div>
          </div>
        </div>
      )}
      {editing !== null && renderMailboxFlyout()}
    </div>
  );
}

function maskTwilioAccountSid(accountSid) {
  if (!accountSid) return 'Default account';
  const trimmed = accountSid.trim();
  if (trimmed.length <= 4) return `••••${trimmed}`;
  return `••••${trimmed.slice(-4)}`;
}

function formatMailboxAge(timestamp) {
  if (!timestamp) return 'Not synced yet';
  const age = Math.max(0, Date.now() - new Date(timestamp).getTime());
  if (!Number.isFinite(age)) return 'Last sync time unavailable';
  const minutes = Math.floor(age / 60_000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  return `${Math.floor(hours / 24)} days ago`;
}

function MailboxSenderRules({
  row,
  canManage,
  saving,
  allowlistDraft,
  blockedSenders,
  onAllowlistDraftChange,
  onAddAllowlist,
  onRemoveAllowlist,
  onBlocklistChange,
}) {
  const allowlist = normalizeSenderAllowlist(row?.sender_allowlist);
  return (
    <section className="space-y-6">
      <div>
        <div className="flex items-center justify-between gap-2"><h3 className="font-semibold">Approved senders</h3>{!canManage && <span className="text-xs uppercase tracking-wide text-gray-500">Read-only</span>}</div>
        <p className="mt-1 text-sm text-gray-500">When this mailbox is set to Bookings only, only approved senders become conversations.</p>
        {allowlist.length ? (
          <ul className="mt-3 flex flex-wrap gap-2">
            {allowlist.map((entry) => <li key={entry} className="flex items-center gap-2 rounded-full border px-3 py-1 text-xs">{entry}{canManage && <button type="button" aria-label={`Remove allowed sender ${entry}`} onClick={() => onRemoveAllowlist(entry)} disabled={saving}>×</button>}</li>)}
          </ul>
        ) : <p className="mt-2 text-sm text-gray-500">No approved senders are set; Bookings only mode imports no mail.</p>}
        {canManage && row && (
          <form className="mt-3 flex gap-2" onSubmit={(event) => { event.preventDefault(); onAddAllowlist(); }}>
            <input value={allowlistDraft} onChange={(event) => onAllowlistDraftChange(event.target.value)} placeholder="name@example.com or @example.com" className="min-w-0 flex-1 rounded border px-3 py-2 text-sm" />
            <button type="submit" disabled={saving} className="rounded-full border px-4 py-2 text-sm">Add</button>
          </form>
        )}
        {!row && <p className="mt-2 text-xs text-gray-500">Save the mailbox before adding sender rules.</p>}
      </div>
      <label className="block text-sm font-semibold">
        Blocked senders
        <span className="mt-1 block text-xs font-normal text-gray-500">Email address or @domain.com, one per line.</span>
        <textarea value={blockedSenders.join('\n')} onChange={(event) => onBlocklistChange(event.target.value)} disabled={!canManage} rows={4} className="mt-2 w-full rounded border px-3 py-2 text-sm" placeholder="spam@example.com" />
      </label>
    </section>
  );
}


function TwilioNumbersPanel({ orgId = null }) {
  const [numbers, setNumbers] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [smsDrafts, setSmsDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [canManage, setCanManage] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [channelFlagsAvailable, setChannelFlagsAvailable] = useState(true);
  const [ownershipEntities, setOwnershipEntities] = useState([]);
  const [ownershipAvailable, setOwnershipAvailable] = useState(false);
  const [numberOwnership, setNumberOwnership] = useState({});

  useEffect(() => {
    let mounted = true;
    async function loadNumbers() {
      if (!orgId) {
        if (mounted) {
          setNumbers([]);
          setLoading(false);
        }
        return;
      }
      setLoading(true);
      setError(null);
      const selectNumbers = (columns) => supabase
        .from('org_twilio_numbers')
        .select(columns)
        .eq('org_id', orgId)
        .order('created_at', { ascending: true });
      let { data, error: dbErr } = await selectNumbers('id, org_id, phone_number, label, twilio_account_sid, sms_twilio_account_sid, sms_enabled, whatsapp_enabled');
      // BL-177 Gate 17: until migration 149 is applied the channel-flag columns don't exist yet —
      // keep the panel working and hide the toggles rather than showing an error.
      const flagsMissing = Boolean(dbErr && /sms_enabled|whatsapp_enabled/.test(dbErr.message || ''));
      if (flagsMissing) {
        ({ data, error: dbErr } = await selectNumbers('id, org_id, phone_number, label, twilio_account_sid, sms_twilio_account_sid'));
      }
      if (!mounted) return;
      setChannelFlagsAvailable(!flagsMissing);
      if (dbErr) {
        setError(dbErr.message);
        setNumbers([]);
      } else {
        const rows = data || [];
        setNumbers(rows);
        setDrafts(Object.fromEntries(rows.map((row) => [row.id, row.twilio_account_sid || ''])));
        setSmsDrafts(Object.fromEntries(rows.map((row) => [row.id, row.sms_twilio_account_sid || ''])));
      }
      setLoading(false);
    }
    loadNumbers().catch((loadError) => {
      if (mounted) {
        setError(loadError.message || 'Could not load Twilio numbers.');
        setLoading(false);
      }
    });
    return () => { mounted = false; };
  }, [orgId]);

  useEffect(() => {
    let mounted = true;
    async function loadOwnership() {
      if (!orgId) return;
      const [entityResult, numberResult] = await Promise.all([
        supabase.from('entities').select('id, name, level_type').eq('org_id', orgId).order('name'),
        supabase.from('org_twilio_numbers').select('id, entity_id').eq('org_id', orgId).order('created_at'),
      ]);
      if (!mounted) return;
      if (entityResult.error || numberResult.error) {
        setError('Number ownership is not available: ' + (entityResult.error || numberResult.error).message);
        return;
      }
      setOwnershipEntities((entityResult.data || []).filter(entity => ['company', 'market'].includes(entity.level_type)));
      setNumberOwnership(Object.fromEntries((numberResult.data || []).map(row => [row.id, row.entity_id || ''])));
      setOwnershipAvailable(true);
    }
    loadOwnership().catch(loadError => { if (mounted) setError('Could not load number ownership: ' + loadError.message); });
    return () => { mounted = false; };
  }, [orgId]);

  useEffect(() => {
    let mounted = true;
    async function loadViewer() {
      const { data: authData } = await supabase.auth.getUser();
      const userId = authData?.user?.id || null;
      if (!mounted) return;
      if (!orgId || !userId) {
        setCanManage(false);
        return;
      }
      const { data: membership } = await supabase
        .from('org_members')
        .select('role')
        .eq('org_id', orgId)
        .eq('user_id', userId)
        .maybeSingle();
      if (mounted) setCanManage(['owner', 'admin'].includes(membership?.role || ''));
    }
    loadViewer().catch(() => {
      if (mounted) setCanManage(false);
    });
    return () => { mounted = false; };
  }, [orgId]);

  async function saveNumberOwnership(numberId, entityId) {
    if (!canManage || !orgId) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const { error: dbErr } = await supabase.from('org_twilio_numbers')
        .update({ entity_id: entityId || null }).eq('id', numberId).eq('org_id', orgId);
      if (dbErr) setError(dbErr.message);
      else {
        setNumberOwnership(current => ({ ...current, [numberId]: entityId || '' }));
        setSuccess('Sending number ownership updated.');
      }
    } catch (saveError) {
      setError('Could not save number ownership: ' + saveError.message);
    } finally {
      setSaving(false);
    }
  }

  async function saveNumberAccount(numberId) {
    if (!canManage || !orgId) return;
    const draft = (drafts[numberId] || '').trim();
    const smsDraft = (smsDrafts[numberId] || '').trim();
    if (draft && !/^AC[A-Za-z0-9]{32}$/.test(draft)) {
      setError('Twilio Account SID must look like AC followed by 32 letters/numbers.');
      return;
    }

    if (smsDraft && !/^AC[0-9a-f]{32}$/.test(smsDraft)) {
      setError('SMS account must start with AC followed by 32 lowercase letters a-f or digits 0-9. Leave it blank to use the existing account.');
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    const { error: dbErr } = await supabase
      .from('org_twilio_numbers')
      .update({ twilio_account_sid: draft || null, sms_twilio_account_sid: smsDraft || null })
      .eq('id', numberId)
      .eq('org_id', orgId);
    if (dbErr) {
      setError(dbErr.message);
    } else {
      setSuccess('Twilio number account updated.');
      setNumbers((current) => current.map((row) =>
        row.id === numberId
          ? { ...row, twilio_account_sid: draft || null, sms_twilio_account_sid: smsDraft || null }
          : row
      ));
    }
    setSaving(false);
  }

  // BL-177 Gate 17: which channels each number may send on. Staff replies pick the number flagged
  // for the channel (SMS/MMS → SMS, WhatsApp → WhatsApp), so these decide the "from" number.
  async function saveChannelFlag(numberId, flag, enabled) {
    if (!canManage || !orgId) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    const { error: dbErr } = await supabase
      .from('org_twilio_numbers')
      .update({ [flag]: enabled })
      .eq('id', numberId)
      .eq('org_id', orgId);
    if (dbErr) {
      setError(dbErr.message);
    } else {
      setSuccess(`${flag === 'sms_enabled' ? 'SMS' : 'WhatsApp'} sending ${enabled ? 'turned on' : 'turned off'} for this number.`);
      setNumbers((current) => current.map((row) => (row.id === numberId ? { ...row, [flag]: enabled } : row)));
    }
    setSaving(false);
  }

  async function repairOldPhotos() {
    if (!canManage || !orgId) return;
    setRepairing(true);
    setError(null);
    setSuccess(null);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      if (!accessToken) throw new Error('You must be signed in to repair photos.');
      const functionUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/twilio-inbound-webhook?action=backfill-media`;
      const apiKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
      let afterId = null;
      let repaired = 0;
      for (let pass = 0; pass < 100; pass += 1) {
        const response = await fetch(functionUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(apiKey ? { apikey: apiKey } : {}),
            Authorization: 'Bearer ' + accessToken,
          },
          body: JSON.stringify({ orgId, afterId, limit: 100 }),
        });
        const body = await response.json().catch(() => ({}));
        if (!response.ok || body?.ok !== true) {
          throw new Error(body?.error || 'Media repair request failed.');
        }
        repaired += Number(body.touchedMedia || 0);
        afterId = body.nextAfterId || null;
        if (!afterId) break;
      }
      if (afterId) {
        throw new Error(
          `Media repair reached the 100-page safety cap with more work remaining. ` +
          `${repaired} photos repaired so far; run repair again to continue.`
        );
      }
      setSuccess(`${repaired} photos repaired.`);
    } catch (repairError) {
      setError(repairError.message || 'Could not repair old photos.');
    } finally {
      setRepairing(false);
    }
  }

  if (loading) return <div className="p-6 text-gray-500">Loading Twilio numbers…</div>;

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold">Twilio Numbers</h2>
          <p className="mt-1 text-sm text-gray-500">
            One phone number can use different Twilio accounts for text messages and WhatsApp.
            Leave SMS account blank to use the existing account; leave both blank to use the default account.
          </p>
        </div>
        {canManage && (
          <button
            type="button"
            onClick={repairOldPhotos}
            disabled={repairing}
            className="rounded border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {repairing ? 'Repairing…' : 'Repair old photos'}
          </button>
        )}
      </div>
      {error && <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
      {success && <div className="rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">{success}</div>}
      {numbers.length === 0 ? (
        <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600">
          No Twilio numbers are configured for this organization yet.
        </div>
      ) : (
        <div className="space-y-3">
          {numbers.map((row) => (
            <div key={row.id} className="rounded border border-gray-200 p-3">
              <div className="text-sm font-medium text-gray-900">{row.label || formatPhone(row.phone_number)}</div>
              <div className="text-xs text-gray-700" data-phone={row.phone_number}>{formatPhone(row.phone_number)}</div>
              {ownershipAvailable && (
                <label className="mt-3 block text-xs text-gray-600">
                  Company / Market ownership
                  <select aria-label={`Ownership for ${row.phone_number}`}
                    value={numberOwnership[row.id] || ''}
                    onChange={event => saveNumberOwnership(row.id, event.target.value)}
                    disabled={!canManage || saving}
                    className="mt-1 block w-full rounded border border-gray-300 px-3 py-2 text-sm">
                    <option value="">Unknown - campaigns admin-only</option>
                    {ownershipEntities.map(entity => <option key={entity.id} value={entity.id}>{entity.name}</option>)}
                  </select>
                </label>
              )}
              {canManage && <LineAlertSettings number={row} />}
              <div className="mt-1 text-xs text-gray-600">
                Current account: {maskTwilioAccountSid(row.twilio_account_sid)}
              </div>
              <div className="mt-1 text-xs text-gray-600">
                SMS account: {row.sms_twilio_account_sid ? maskTwilioAccountSid(row.sms_twilio_account_sid) : 'Same as existing account'}
              </div>
              {channelFlagsAvailable && (
                <div className="mt-3 flex flex-wrap items-center gap-4" role="group" aria-label={`Channels for ${row.phone_number}`}>
                  {[
                    ['sms_enabled', 'SMS'],
                    ['whatsapp_enabled', 'WhatsApp'],
                  ].map(([flag, label]) => (
                    <label key={flag} className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        role="switch"
                        aria-label={`${label} for ${row.phone_number}`}
                        checked={row[flag] === true}
                        onChange={(event) => saveChannelFlag(row.id, flag, event.target.checked)}
                        disabled={!canManage || saving}
                        className="h-4 w-4 accent-[#22C55E]"
                      />
                      {label}
                    </label>
                  ))}
                </div>
              )}
              <div className="mt-3 flex flex-wrap items-end gap-2">
                <label className="flex-1 text-xs text-gray-600">
                  Twilio Account SID (optional)
                  <span className="block font-normal">WhatsApp uses this account. Texts use it too unless an SMS account is set.</span>
                  <input
                    type="text"
                    aria-label={`Twilio account for ${row.phone_number}`}
                    value={drafts[row.id] || ''}
                    onChange={(event) => setDrafts((current) => ({ ...current, [row.id]: event.target.value }))}
                    disabled={!canManage || saving}
                    placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  />
                </label>
                <label className="flex-1 text-xs text-gray-600">
                  SMS account (optional)
                  <span className="block font-normal">The account hosting this number&apos;s SMS and picture messages.</span>
                  <input
                    type="text"
                    aria-label={`SMS account for ${row.phone_number}`}
                    value={smsDrafts[row.id] || ''}
                    onChange={(event) => setSmsDrafts((current) => ({ ...current, [row.id]: event.target.value }))}
                    disabled={!canManage || saving}
                    placeholder="Same as existing account"
                    className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  />
                </label>
                {canManage && (
                  <button
                    type="button"
                    onClick={() => saveNumberAccount(row.id)}
                    disabled={saving}
                    aria-label={`Save accounts for ${row.phone_number}`}
                    className="rounded-full border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Save
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function EmailChannelSettingsView({ theme = 'light', orgId = null }) {
    // BL-177 Gate 12: keep the active tab in the URL (?tab=ai-assistant) so a refresh stays put.
    // Plain window.location/history rather than react-router's useSearchParams — this view isn't
    // otherwise route-aware and the plain form avoids a Jest/ESM resolution gap with react-router
    // v7's subpath exports.
    const readTabFromUrl = () => {
        if (typeof window === 'undefined') return 'email';
        const params = new URLSearchParams(window.location.search);
        const tab = params.get('tab');
        if (tab === 'numbers') return 'numbers';
        if (tab === 'ai-assistant') return 'ai-assistant';
        if (tab === 'templates' || params.get('section') === 'templates') return 'templates';
        if (tab === 'customer-consent') return 'customer-consent';
        if (tab === 'verification') return 'verification';
        return 'email';
    };
    const [activeTab, setActiveTab] = useState(readTabFromUrl);
    const dark = theme === 'dark';

    const changeTab = (value) => {
        setActiveTab(value);
        if (typeof window === 'undefined') return;
        const params = new URLSearchParams(window.location.search);
        if (value === 'email') params.delete('tab');
        else params.set('tab', value);
        params.delete('section');
        const query = params.toString();
        const nextUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
        window.history.replaceState(window.history.state, '', nextUrl);
        window.dispatchEvent(new PopStateEvent('popstate'));
    };

    return (
        <div className={dark ? 'text-gray-100' : 'text-gray-800'}>
            <div className={`border-b px-6 pt-3 ${dark ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="mt-4 flex flex-wrap gap-1" role="tablist" aria-label="Communications settings">
                    {[
                        ['email', 'Mailboxes'],
                        ['numbers', 'Phone numbers'],
                        ['templates', 'Templates'],
                        ['customer-consent', 'Customer consent'],
                        ['verification', 'Verification codes'],
                    ].map(([value, label]) => (
                        <button
                            key={value}
                            type="button"
                            role="tab"
                            aria-selected={activeTab === value}
                            onClick={() => changeTab(value)}
                            className={`border-b-2 px-4 py-2 text-sm font-medium ${activeTab === value ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            </div>
            <div role="tabpanel">
                {activeTab === 'verification'
                    ? <VerificationCodeSettings orgId={orgId} />
                    : activeTab === 'email'
                    ? <EmailConnectionsPanel theme={theme} orgId={orgId} />
                    : activeTab === 'numbers'
                        ? <TwilioNumbersPanel orgId={orgId} />
                    : activeTab === 'ai-assistant'
                        // BL-177 Step 2m: the AI controls live in Business schedule now. An old bookmark lands here and is pointed there.
                        ? <div className="p-6 text-sm text-gray-600" data-testid="ai-moved">The AI Assistant controls moved to <a className="text-[#03B7C9] underline" href="/admin/business-schedule#ai-integration">Business Schedule › AI integration</a>.</div>
                    : activeTab === 'templates'
                        ? <MessageTemplatesSettingsView theme={theme} orgId={orgId} />
                        : <div className="p-6 text-sm text-gray-600">SMS consent and opt-out history are managed on each CRM contact profile. Open <a className="text-[#03B7C9] underline" href="/clients">Clients</a> to review a customer’s text-message settings.</div>}
            </div>
        </div>
    );
}

function Field({ label, value, onChange, placeholder, type = 'text', onBlur }) {
  return (
    <div className="mb-3">
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:border-blue-500"
      />
    </div>
  );
}
